/**
 * WorkerProductBoundary — the worker portal's licence gate for a whole route.
 *
 * Same contract as `AdminProductBoundary`, different source of truth: the worker
 * portal already resolves its licence through `useRoleProfile`, which fetches
 * profile + manager-overview + dashboard once and shares the result from a
 * module-level cache. Reading the same store means the sidebar and the route
 * gate cannot disagree — the drift class svdy-rules §10.5b is about.
 *
 * `role === null` is the unknown state (first paint, or every fetch failed): a
 * spinner, then a redirect once the answer is in. Fail-closed, because the two
 * routes behind this — `/me/face-template` and `/me/clock-in-reviews` — invite a
 * worker to submit or review biometric data, which a PureOA-only org has no
 * lawful basis to process.
 *
 * Renders no copy in any state, so it adds nothing to the nine locale files.
 */
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route } from 'react-router-dom';

import { useRoleProfile } from '../hooks/useRoleProfile';
import { hasProduct, type ProductCode } from './productAccess';
import { WORKER_SURFACE_PRODUCT } from './workerSurfaceLicence';

interface WorkerProductBoundaryProps {
  product: ProductCode;
  /** Where an unlicensed worker lands. The worker portal's home by default. */
  fallback?: string;
  children: React.ReactNode;
}

const WorkerProductBoundary: React.FC<WorkerProductBoundaryProps> = ({
  product,
  fallback = '/me/dashboard',
  children,
}) => {
  const { role, loading } = useRoleProfile();

  if (loading || role === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!hasProduct({ products: role.licensedProducts, resolved: true }, product)) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

export default WorkerProductBoundary;

/**
 * A worker-portal `<Route>` whose product boundary is DERIVED, not typed.
 *
 * Written 2026-09-04 because three routes had shipped with a sidebar guard and no boundary. With the
 * product looked up from `WORKER_SURFACE_PRODUCT` — the same map the sidebar reads — that class of
 * defect stops being possible: you cannot add a route through this helper and forget the gate, and you
 * cannot give it a product the sidebar disagrees with.
 *
 * `segment` is the path relative to `/me`, exactly as react-router wants it, and the lookup key is
 * `/me/${segment}` so a reader can grep either form.
 *
 * A surface with no entry in the map resolves to NO boundary here and to INVISIBLE in the sidebar —
 * asymmetric on purpose. `licenceGateLayers.test.ts` asserts every routed segment has an entry, so the
 * asymmetry is a build failure rather than a silent hole.
 */
export function workerRoute(segment: string, element: React.ReactNode): React.ReactElement {
  const product = WORKER_SURFACE_PRODUCT[`/me/${segment}`];
  return (
    <Route
      path={segment}
      element={product ? <WorkerProductBoundary product={product}>{element}</WorkerProductBoundary> : element}
    />
  );
}
